import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApiClient } from '@/services/adminApi';
import {
  Shield,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Settings,
  Users,
  Fingerprint,
} from 'lucide-react';
import { Skeleton } from '@connor-music/shared';

interface SsoStatus {
  active_provider: string | null;
  cognito_entity_id: string;
  cognito_acs_url: string;
  providers: Record<string, { configured: boolean; enabled: boolean; config?: Record<string, unknown> }>;
}

const PROVIDER_LABELS: Record<string, string> = {
  'microsoft-entra': 'Microsoft Entra ID',
  'google-workspace': 'Google Workspace',
};

export default function AdminDashboard() {
  const [ssoStatus, setSsoStatus] = useState<SsoStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const status = await adminApiClient.getSsoStatus();
        setSsoStatus(status);
      } catch (err) {
        console.error('Failed to load SSO status:', err);
        setError('Failed to load SSO status. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    loadStatus();
  }, []);

  const isSsoConfigured = ssoStatus?.active_provider !== null;

  const setupSteps = [
    {
      number: 1,
      title: 'Configure Identity Provider',
      description: 'Set up Microsoft Entra ID or Google Workspace as your SSO provider.',
      completed: ssoStatus
        ? Object.values(ssoStatus.providers).some((p) => p.configured)
        : false,
      link: '/admin-setup/dashboard/identity',
    },
    {
      number: 2,
      title: 'Map Roles to Groups',
      description: 'Assign IdP groups to Connor Admin and Employee roles.',
      completed: isSsoConfigured,
      link: '/admin-setup/dashboard/identity',
    },
    {
      number: 3,
      title: 'Activate SSO',
      description: 'Enable SSO login for all Connor Consulting employees.',
      completed: isSsoConfigured,
      link: '/admin-setup/dashboard/identity',
    },
    {
      number: 4,
      title: 'Configure Portal Branding',
      description: 'Customize the portal name, logos, and favicon.',
      completed: false,
      link: '/admin-setup/dashboard/configuration',
    },
  ];

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-4 w-80" />
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-48" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Skeleton className="h-16 rounded-md" />
            <Skeleton className="h-16 rounded-md" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
              <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Admin Setup Dashboard
            </h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Complete the steps below to configure your portal. Once SSO is active, employees can
            sign in with their corporate accounts.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* SSO Status Card */}
        <div className="mb-8 rounded-lg border bg-white shadow dark:border-gray-700 dark:bg-gray-800 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            SSO Status
          </h2>
          {isSsoConfigured ? (
            <div className="flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-green-500 dark:text-green-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  SSO is active
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Provider: {PROVIDER_LABELS[ssoStatus?.active_provider ?? ''] ?? ssoStatus?.active_provider}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-amber-500 dark:text-amber-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  SSO is not configured
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Complete the setup steps below to enable employee SSO login.
                </p>
              </div>
            </div>
          )}

          {ssoStatus && (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 p-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Entity ID
                </p>
                <p className="mt-1 text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
                  {ssoStatus.cognito_entity_id || 'Not set'}
                </p>
              </div>
              <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 p-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  ACS URL
                </p>
                <p className="mt-1 text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
                  {ssoStatus.cognito_acs_url || 'Not set'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Setup Steps */}
        {!isSsoConfigured && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Setup Steps
            </h2>
            <div className="space-y-3">
              {setupSteps.map((step) => (
                <Link
                  key={step.number}
                  to={step.link}
                  className="flex items-center gap-4 rounded-lg border bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750"
                >
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      step.completed
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {step.completed ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      step.number
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {step.title}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {step.description}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Link
            to="/admin-setup/dashboard/identity"
            className="flex items-center gap-3 rounded-lg border bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750"
          >
            <Fingerprint className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Identity Providers
            </span>
          </Link>
          <Link
            to="/admin-setup/dashboard/configuration"
            className="flex items-center gap-3 rounded-lg border bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750"
          >
            <Settings className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Configuration
            </span>
          </Link>
          <Link
            to="/admin-setup/dashboard/users"
            className="flex items-center gap-3 rounded-lg border bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750"
          >
            <Users className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              User Management
            </span>
          </Link>
        </div>
    </div>
  );
}
